import { Dropdown as AntdDropdown, DropdownProps as AntdDropdownProps } from 'antd';
import { forwardRef, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';

import { Divider } from '../../components/Divider';
import { Panel } from '../../components/Panel';
import { datasetToAttributes } from '../../utils';
import { Icon } from '../Icon';
import { Text } from '../Text';
import {
  StyledCustomDropdownItemWrapper,
  StyledDropDownMenu,
  StyledDropdownButton,
  StyledDropdownTriggerWrapper,
  StyledMenuItemCenterWrapper,
  StyledMenuItemSideWrapper,
  StyledMenuItemWrapper,
} from './DropdownNext.style';
import type { DropdownNextOptionProps, DropdownNextProps } from './DropdownNext.types';

export { DropdownNextProps, DropdownNextOptionProps };

export const DropdownOption = memo(function DropdownOption({
  variant,
  disabled,
  label,
  description,
  extra,
  icon,
  children,
  action,
  danger,
  dataset,
  isClosedOnSelect,
  onClick,
  value,
}: DropdownNextOptionProps) {
  const theme = useTheme();

  const color = useMemo(() => {
    if (disabled) {
      return theme.Color.Neutrals.Gray;
    }

    if (danger) {
      return theme.Color.Feedback.Alert;
    }

    if (variant === 'selected') {
      return theme.Color.Brand.Primary;
    }

    return theme.Color.Neutrals.Dark;
  }, [theme, variant, disabled, danger]);

  const handleClick = useCallback(
    (value: number | string, closeOnSelect?: boolean) => {
      if (!disabled) {
        onClick?.(value, closeOnSelect);
      }
    },
    [disabled, onClick]
  );

  const dataProps = useMemo(() => datasetToAttributes(dataset), [dataset]);

  return (
    <StyledMenuItemWrapper
      data-action={action}
      variant={variant}
      disabled={disabled}
      onClick={() => handleClick(value, isClosedOnSelect)}
      {...dataProps}
    >
      {icon && (
        <Icon color={icon.color ?? color} name={icon.name} size={'XSmall'} angle={icon.angle} />
      )}

      <StyledMenuItemCenterWrapper>
        <Text size={'Body'} color={color}>
          {label}
        </Text>

        {description && (
          <Text size={'Body'} color={theme.Color.Neutrals.Gray}>
            {description}
          </Text>
        )}
      </StyledMenuItemCenterWrapper>

      {extra && <StyledMenuItemSideWrapper>{extra}</StyledMenuItemSideWrapper>}

      {children && (
        <StyledMenuItemSideWrapper>
          <Icon name={'editor-chevron-right'} color={color} size={'XXSmall'} />
        </StyledMenuItemSideWrapper>
      )}
    </StyledMenuItemWrapper>
  );
});

export function DropdownItem({
  variant = 'default',
  disabled,
  value,
  label,
  description,
  extra,
  icon,
  action,
  dataset,
  onClick,
  isClosedOnSelect,
  children,
  danger = false,
  onOpenChange,
  state,
  trigger = 'hover',
  width,
  getPopupContainer,
  placement = 'rightTop',
}: DropdownNextOptionProps) {
  if (Array.isArray(children)) {
    return (
      <DropdownNext
        placement={placement}
        options={children}
        trigger={trigger}
        onClick={onClick}
        isClosedOnSelect={isClosedOnSelect}
        onOpenChange={onOpenChange}
        state={state}
        disabled={!!disabled}
        width={width}
        getPopupContainer={getPopupContainer}
      >
        <div>
          <DropdownOption
            variant={variant}
            disabled={disabled}
            label={label}
            description={description}
            value={value}
            extra={extra}
            action={action}
            icon={icon}
            children={children}
            danger={danger}
            dataset={dataset}
            onClick={() => onClick && onClick(value, isClosedOnSelect)}
          />
        </div>
      </DropdownNext>
    );
  }

  return (
    <DropdownOption
      variant={variant}
      description={description}
      isClosedOnSelect={isClosedOnSelect}
      disabled={!!disabled}
      label={label}
      value={value}
      extra={extra}
      icon={icon}
      danger={danger}
      action={action}
      dataset={dataset}
      onClick={() => onClick && onClick(value, isClosedOnSelect)}
    />
  );
}

const TriggerWrapper = forwardRef<
  HTMLDivElement,
  {
    children: React.ReactElement;
  }
>(({ children, ...props }, ref) => {
  return (
    <StyledDropdownTriggerWrapper ref={ref} {...props}>
      {children}
    </StyledDropdownTriggerWrapper>
  );
});

TriggerWrapper.displayName = 'DropdownTrigger';

export function DropdownNext({
  placement,
  children: childrenProp,
  getPopupContainer,
  options,
  fixedHeight,
  trigger = 'click',
  onClick: onClickProp,
  state: stateProps,
  onOpenChange: onOpenChangeProp,
  isClosedOnSelect = false,
  alwaysOpen = false,
  disabled = false,
  width,
  displayMode = 'default',
  gridColumns = 2,
  widthVariant = 'default',
  triggerRef = true,
}: DropdownNextProps) {
  const [isOpen, setIsOpen] = useState(!!stateProps?.open);

  useEffect(() => {
    if (stateProps?.open !== undefined) {
      setIsOpen(stateProps.open);
    }
  }, [stateProps]);

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (alwaysOpen) {
        return;
      }

      setIsOpen(open);
      onOpenChangeProp?.(open);
    },
    [onOpenChangeProp, alwaysOpen]
  );

  const handleClick = useCallback(
    (onClick?: () => void) => (value: number | string, closeOnSelect?: boolean) => {
      closeOnSelect && onOpenChange(false);
      onClickProp?.(value, closeOnSelect);
      onClick?.();
    },
    [onClickProp, onOpenChange]
  );

  const buttonRef = useRef<HTMLDivElement>(null);

  const children = useMemo(() => {
    if (width === 'matchButton') {
      return <StyledDropdownButton ref={buttonRef}>{childrenProp}</StyledDropdownButton>;
    } else {
      return childrenProp;
    }
  }, [childrenProp, width]);

  const buttonWidth = useMemo(() => {
    if (typeof width === 'number') {
      return width;
    }

    if (width === 'matchButton' && buttonRef.current) {
      return buttonRef.current.getBoundingClientRect().width;
    }

    return;
    // buttonRef.current is required here so the width would update when buttonRef changed/rendered
  }, [width, children, buttonRef.current]);

  if (!options.length) {
    return null;
  }

  return (
    <AntdDropdown
      {...(getPopupContainer ? { getPopupContainer } : {})}
      disabled={disabled}
      open={isOpen}
      onOpenChange={onOpenChange}
      placement={placement as AntdDropdownProps['placement']}
      trigger={[trigger]}
      popupRender={() => {
        return (
          <Panel variant="float" widthVariant={widthVariant}>
            <Panel.Content>
              <StyledDropDownMenu
                $fixedHeight={fixedHeight}
                $fixedWidth={buttonWidth}
                $displayMode={displayMode}
                $gridColumns={gridColumns}
              >
                {options.map((item, index) => {
                  switch (item.type) {
                    case 'divider':
                      return <Divider key={`dropdown-option-divider-${index}`} />;

                    case 'custom':
                      return (
                        <StyledCustomDropdownItemWrapper
                          key={`dropdown-option-${item.value}-${index}`}
                          onClick={() => handleClick()(item.value, isClosedOnSelect)}
                          {...datasetToAttributes(item.dataset)}
                        >
                          {item.children}
                        </StyledCustomDropdownItemWrapper>
                      );

                    default:
                      return (
                        <DropdownItem
                          variant={item.variant}
                          disabled={item.disabled}
                          value={item.value}
                          label={item.label}
                          description={item.description}
                          extra={item.extra}
                          icon={item.icon}
                          action={item.action}
                          dataset={item.dataset}
                          isClosedOnSelect={item.isClosedOnSelect}
                          danger={item.danger}
                          children={item.children}
                          onClick={() =>
                            handleClick(item.onClick as () => void)(item.value, isClosedOnSelect)
                          }
                          key={`dropdown-option-${item.value}-${index}`}
                          onOpenChange={item.onOpenChange || onOpenChangeProp}
                          state={item.state}
                          trigger={item.trigger}
                          width={item.width}
                          getPopupContainer={item.getPopupContainer}
                          placement={item.placement}
                        />
                      );
                  }
                })}
              </StyledDropDownMenu>
            </Panel.Content>
          </Panel>
        );
      }}
    >
      {children && triggerRef ? <TriggerWrapper>{children}</TriggerWrapper> : children}
    </AntdDropdown>
  );
}
